import { PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import {
  Button,
  Divider,
  Input,
  Modal,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { priceToCurrency, getPhotoThumbnailUrl } from "utils/common";
import Actions from "./components/Actions";
import VoucherDetailModal from "./components/DetailModal";
import * as voucherActions from "./slice";

function Voucher() {
  const dispatch = useDispatch();
  const { vouchers, pagination, isLoading, isProcessing, isCompleted } =
    useSelector((state) => state.expressVoucher);

  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectingVoucher, setSelectingVoucher] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [searchIsPersonal, setSearchIsPersonal] = useState("");
  const [searchIsSecret, setSearchIsSecret] = useState("");
  const [searchIsActive, setSearchIsActive] = useState(true);
  const [randomModalKey, setRandomModalKey] = useState(new Date().getTime());

  useEffect(() => {
    dispatch(
      voucherActions.fetchVouchers({
        ...pagination,
        name: searchName,
        isPersonal: searchIsPersonal,
        isSecret: searchIsSecret,
        isActive: searchIsActive,
        current: 1,
      })
    );
  }, []);

  useEffect(() => {
    if (isCompleted) {
      setOpenDetailModal(false);
      setSelectingVoucher(null);
      dispatch(
        voucherActions.fetchVouchers({
          ...pagination,
          name: searchName,
          isPersonal: searchIsPersonal,
          isSecret: searchIsSecret,
          isActive: searchIsActive,
          current: 1,
        })
      );
    }
  }, [isCompleted]);

  const onCreateItem = () => {
    setSelectingVoucher(null);
    setRandomModalKey(new Date().getTime());
    setOpenDetailModal(true);
  };

  const onEdit = (voucher) => {
    setSelectingVoucher(voucher);
    setOpenDetailModal(true);
  };

  const onDelete = (voucher) => {
    Modal.confirm({
      title: `Xác nhận xoá voucher`,
      content: `Bạn có chắc chắn muốn xoá voucher này?`,
      onOk: () => dispatch(voucherActions.deleteVoucher(voucher._id)),
      centered: true,
    });
  };

  const onSubmit = (voucher, formData = null) => {
    if (voucher._id) {
      dispatch(voucherActions.updateVoucher(voucher, formData));
    } else {
      dispatch(voucherActions.createVoucher(voucher, formData));
    }
  };

  const onCancel = () => {
    setOpenDetailModal(false);
    setSelectingVoucher(null);
    dispatch(voucherActions.setIsProcessing(false));
  };

  const onChangeNameSearch = (e) => {
    setSearchName(e.target.value);
  };

  const onSearch = (kw) => {
    dispatch(
      voucherActions.fetchVouchers({
        ...pagination,
        name: kw,
        isPersonal: searchIsPersonal,
        isSecret: searchIsSecret,
        isActive: searchIsActive,
        current: 1,
      })
    );
  };

  const onTableChange = async (pagination) => {
    dispatch(
      voucherActions.fetchVouchers({
        ...pagination,
        name: searchName,
        isPersonal: searchIsPersonal,
        isSecret: searchIsSecret,
        isActive: searchIsActive,
      })
    );
  };

  const handleSeachIsPersonal = (isPersonal) => {
    setSearchIsPersonal(isPersonal);
    dispatch(
      voucherActions.fetchVouchers({
        isPersonal,
        ...pagination,
        name: searchName,
        isSecret: searchIsSecret,
        isActive: searchIsActive,
      })
    );
  };

  const handleSeachIsSecret = (isSecret) => {
    setSearchIsSecret(isSecret);
    dispatch(
      voucherActions.fetchVouchers({
        ...pagination,
        isSecret,
        name: searchName,
        isPersonal: searchIsPersonal,
        isActive: searchIsActive,
        current: 1,
      })
    );
  };

  const handleSearchIsActive = (isActive) => {
    setSearchIsActive(isActive);
    dispatch(
      voucherActions.fetchVouchers({
        ...pagination,
        isActive,
        name: searchName,
        isPersonal: searchIsPersonal,
        isSecret: searchIsSecret,
        current: 1,
      })
    );
  };

  const columns = [
    {
      title: "STT",
      align: "center",
      render: (text, record, index) => (
        <Typography.Paragraph
          copyable={{
            text: record._id,
            tooltips: ["Sao chép ID", "Đã sao chép!"],
          }}
        >
          {(pagination.current - 1) * pagination.pageSize + index + 1}
        </Typography.Paragraph>
      ),
    },
    {
      title: "Mã voucher",
      render: (record) => (
        <>
          <span>{record.code}</span>
          {record.autoApply && (
            <Tag
              color="orange"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginTop: "4px",
                fontSize: "12px",
                lineHeight: "1.2",
                borderRadius: "4px",
                padding: "2px 6px",
                width: "fit-content",
              }}
            >
              <ThunderboltOutlined style={{ fontSize: "11px" }} />
              Tự động áp dụng
            </Tag>
          )}
        </>
      ),
    },
    {
      title: "Tên voucher",
      // dataIndex: "name",
      render: (record) => (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "4px",
            }}
          >
            {record.image && (
              <img
                src={getPhotoThumbnailUrl(record.image.path)}
                alt="voucher"
                style={{
                  width: "40px",
                  height: "40px",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
            )}
            <span>{record.name}</span>
          </div>
          {record.isSecret ? (
            <Tag
              color="purple"
              style={{ display: "inline-block", marginTop: "4px" }}
            >
              Mã bí mật
            </Tag>
          ) : null}
          {record.isPersonal ? (
            <Tag
              color="magenta"
              style={{ display: "inline-block", marginTop: "4px" }}
            >
              Mã cá nhân
            </Tag>
          ) : null}
        </>
      ),
    },
    {
      title: "Giá trị giảm",
      // dataIndex: "value",
      render: (record) =>
        record.discountValueType === "direct"
          ? priceToCurrency(record.value)
          : record.value + "%",
    },
    {
      title: "Số lượng",
      render: (record) => `${record.usedCount}/${record.count}`,
    },
    {
      title: "Ngày bắt đầu",
      dataIndex: "startDate",
      width: "7%",
      render: (startDate) => moment(startDate).format("DD/MM/YYYY"),
    },
    {
      title: "Ngày kết thúc",
      dataIndex: "endDate",
      width: "7%",
      render: (endDate) => moment(endDate).format("DD/MM/YYYY"),
    },
    {
      title: "Trạng thái",
      dataIndex: "active",
      align: "center",
      render: (active) =>
        active ? (
          <Tag color="#4caf50">Active</Tag>
        ) : (
          <Tag color="#f44336">In-active</Tag>
        ),
    },
    {
      title: "Thao tác",
      align: "center",
      render: (record) => (
        <Actions record={record} onEdit={onEdit} onDelete={onDelete} />
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle">
        <Typography.Title level={2}>
          [{pagination?.total || "0"}] Voucher khuyến mãi
        </Typography.Title>
        <Space size="middle">
          <Input.Search
            placeholder="Tìm theo tên hoặc code"
            onChange={onChangeNameSearch}
            onSearch={onSearch}
            style={{ width: 330 }}
            enterButton
            allowClear
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreateItem}>
            Thêm mới
          </Button>
        </Space>
      </Row>
      <Row justify="end" align="middle">
        <Space size="middle">
          <Switch
            checkedChildren="Mã cá nhân"
            unCheckedChildren="Mã cá nhân"
            onChange={handleSeachIsPersonal}
          />
          <Switch
            checkedChildren="Mã bí mật"
            unCheckedChildren="Mã bí mật"
            onChange={handleSeachIsSecret}
          />
          <Switch
            defaultChecked
            checkedChildren="Đang kích hoạt"
            unCheckedChildren="Hủy kích hoạt"
            onChange={handleSearchIsActive}
          />
        </Space>
      </Row>
      <Divider />
      <Table
        bordered
        loading={isLoading}
        columns={columns}
        dataSource={vouchers}
        rowKey={(record) => record._id}
        pagination={pagination}
        onChange={onTableChange}
      />
      <VoucherDetailModal
        open={openDetailModal}
        isProcessing={isProcessing}
        onSubmit={onSubmit}
        onCancel={onCancel}
        voucher={selectingVoucher}
        key={selectingVoucher ? selectingVoucher["_id"] : randomModalKey}
      />
    </>
  );
}

export default Voucher;
